import dayjs from 'dayjs';
import { normalizeWorthMatchInput } from 'src/components/ui/DefaultForm/hooks/worthOptions';

const CONTACT_KIND_MAP = {
	work: 'WORK',
	personal: 'PERSONAL',
};

const WORTH_MATCH_KEYS = new Set(['corporative', 'graduation']);

const resolveWorthValue = (key, field, defaults = {}) => {
	const value = field?.value;

	if (
		field?.isAuto ||
		value === 'auto' ||
		value === undefined ||
		value === null
	) {
		const fallback = defaults[key] ?? null;

		return WORTH_MATCH_KEYS.has(key)
			? normalizeWorthMatchInput(fallback)
			: fallback;
	}

	if (key === 'value') {
		return Number(value);
	}

	if (WORTH_MATCH_KEYS.has(key)) {
		return normalizeWorthMatchInput(value);
	}

	return value;
};

const mapFileInput = (files) => {
	const file = files?.[0];

	if (!file) return null;

	if (file instanceof File || file instanceof Blob) {
		return {
			upload: file,
			name: file.name,
		};
	}

	if (file.id) {
		return {
			id: String(file.id),
			name: file.name,
		};
	}

	return null;
};

const mapPersonalContact = (data, { id } = {}) => {
	const contact = {
		firstName: data.name,
		lastName: data.secondName,
		middleName: data.thirdName || undefined,
		position: data.job?.label || String(data.job?.value ?? ''),
		responsiblePerson: data.decisionMaker === '1',
		active: data.status?.value !== 'unavail',
		comment: data.comment || undefined,
	};

	if (id) {
		contact.id = String(id);
	}

	if (data.contacts?.length) {
		contact.numbers = data.contacts
			.filter(({ text }) => text)
			.map(({ text, type }) => ({
				number: text,
				kind: CONTACT_KIND_MAP[type?.value] || 'WORK',
			}));
	}

	return contact;
};

const mapGeneralNumber = (data, { id, active = true } = {}) => {
	const number = {
		active,
		number: data.phone,
		comment: data.comment || '',
	};

	if (id) {
		number.id = String(id);
	}

	return number;
};

const mapPayloadItem = (item, worthDefaults) => {
	const { type, action, data } = item;

	switch (type) {
		case 'objection':
			if (action === 'create') {
				return {
					objections: [
						{
							typeId: Number(data.objection?.id),
							comment: data.comment || '',
						},
					],
				};
			}

			if (action === 'remove') {
				return {
					objections: [
						{
							id: String(data.objection?.id),
							typeId: Number(data.objection?.typeId),
							comment: data.objection?.comment || '',
							solved: true,
						},
					],
				};
			}
			break;

		case 'contact':
			if (action === 'create') {
				return {
					personalContacts: [mapPersonalContact(data)],
				};
			}

			if (action === 'change') {
				if (data.target?.isPerson) {
					return {
						personalContacts: [
							mapPersonalContact(data, { id: data.target.id }),
						],
					};
				}

				return {
					generalNumbers: [
						mapGeneralNumber(data, {
							id: data.target?.id,
							active: data.status?.value === 'avail',
						}),
					],
				};
			}
			break;

		case 'number':
			if (action === 'create') {
				return { generalNumbers: [mapGeneralNumber(data)] };
			}
			break;

		case 'contract': {
			const file = mapFileInput(data.files);

			if (!file) return null;

			return {
				contracts: [
					{
						name: data.title,
						signed: data.signed === '1',
						file,
					},
				],
			};
		}

		case 'doc': {
			const file = mapFileInput(data.files);

			if (!file) return null;

			return {
				documents: [
					{
						name: data.title,
						file,
					},
				],
			};
		}

		case 'params':
			return {
				companyParams: {
					knownFrom: data.date
						? dayjs(data.date).format('YYYY-MM-DD')
						: '',
					email: (data.emails || []).filter(Boolean),
					sitesExternal: (data.sites || []).filter(Boolean),
				},
			};

		case 'meeting':
			return {
				meetings: [
					{
						date: dayjs(data.date).format('YYYY-MM-DD'),
						time: dayjs(data.time).format('HH:mm'),
						place: data.place,
					},
				],
			};

		case 'messaging':
			return {
				messagings: [
					{
						date: dayjs(data.date).format('YYYY-MM-DD'),
					},
				],
			};

		case 'worth':
			return {
				worth: {
					value: resolveWorthValue(
						'value',
						data.worth,
						worthDefaults,
					),
					wedding: resolveWorthValue(
						'wedding',
						data.wedding,
						worthDefaults,
					),
					birthday: resolveWorthValue(
						'birthday',
						data.birthday,
						worthDefaults,
					),
					corporative: resolveWorthValue(
						'corporative',
						data.corporate,
						worthDefaults,
					),
					newYear: resolveWorthValue(
						'newYear',
						data.newYear,
						worthDefaults,
					),
					graduation: resolveWorthValue(
						'graduation',
						data.graduation,
						worthDefaults,
					),
				},
			};

		case 'companyGroup': {
			const ids = (data.areas || [])
				.map((area) => area?.value)
				.filter(Boolean)
				.map(String);

			if (!ids.length) return null;

			if (action === 'link') {
				return { companyGroupLinks: { link: ids } };
			}

			if (action === 'unlink') {
				return { companyGroupLinks: { unlink: ids } };
			}

			break;
		}

		default:
			return null;
	}

	return null;
};

const mergePayload = (target, source) => {
	if (!source) return;

	for (const [key, value] of Object.entries(source)) {
		if (key === 'companyGroupLinks' && value && typeof value === 'object') {
			target.companyGroupLinks = target.companyGroupLinks || {};

			if (value.link?.length) {
				target.companyGroupLinks.link = [
					...(target.companyGroupLinks.link || []),
					...value.link,
				];
			}

			if (value.unlink?.length) {
				target.companyGroupLinks.unlink = [
					...(target.companyGroupLinks.unlink || []),
					...value.unlink,
				];
			}

			continue;
		}

		if (Array.isArray(value)) {
			target[key] = [...(target[key] || []), ...value];
		} else if (value && typeof value === 'object') {
			target[key] = { ...(target[key] || {}), ...value };
		} else {
			target[key] = value;
		}
	}
};

export const mapActivityPayload = ({
	companyId,
	comment,
	status,
	statusLossReason,
	statusPostponedReason,
	statusPostponedUntil,
	payloadItems = [],
	worthDefaults = {},
}) => {
	const input = {
		companyId: Number(companyId),
		comment: comment.trim(),
	};

	if (status?.value != null && status.value !== -1) {
		input.companyStatusId = Number(status.value);
	}

	if (statusLossReason?.value) {
		input.statusLossReason = statusLossReason.value;
	}

	if (statusPostponedReason?.value) {
		input.statusPostponedReason = statusPostponedReason.value;
	}

	if (statusPostponedUntil) {
		const date = dayjs(statusPostponedUntil);
		if (date.isValid()) {
			input.statusPostponedUntil = date.endOf('day').toISOString();
		}
	}

	const merged = {};

	for (const item of payloadItems) {
		mergePayload(merged, mapPayloadItem(item, worthDefaults));
	}

	return { ...input, ...merged };
};
